import React, { useEffect, useRef, useState } from "react";
import {
  getNotificationApi,
  getNotificationCountApi,
  markReadNotificationApi,
} from "./Notification.api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "context/auth-context";
import PointUIModel from "./PointUIModel";
import SurveyUIModel from "./SurveyUIModel";
import { useTranslation } from "react-i18next";
import { FiBell, FiX } from "react-icons/fi";

export const ShowNotificationModel = ({
  showNotificationModel,
  setShowNotificationModel,
  setNotificationCount,
  notificationCount,
  supportCount = 0,
  supportContent,
}: any) => {
  const [activeTab, setActiveTab] = React.useState(supportCount > 0 ? "Support" : "Rewards");
  const [pointNotiCount, setPointNotiCount] = useState([{ count: 0 }]);
  const [surveyNotiCount, setSurveyNotiCount] = useState([{ count: 0 }]);
  const [tabChangeCount, setTabChangeCount] = useState(0);
  const { t } = useTranslation();
  const { auth } = useAuth();
  const queryClient = useQueryClient();
  const modalRef = useRef<HTMLDivElement>(null);
  const type = activeTab === "Rewards" ? "point" : "survey";

  //notification count
  const getNotificationCount = useQuery(
    ["notificationCount", auth.panelistId, activeTab],
    () => getNotificationCountApi(auth.panelistId),
    {
      refetchOnWindowFocus: false,
    }
  );

  // Use `refetch` whenever the modal state changes
  useEffect(() => {
    if (getNotificationCount?.data) {
      setNotificationCount(getNotificationCount?.data?.unreadCount);
      setPointNotiCount(getNotificationCount.data.pointCount || [{ count: 0 }]);
      setSurveyNotiCount(getNotificationCount.data.surveyCount || [{ count: 0 }]);
    }
  }, [getNotificationCount?.data, activeTab, tabChangeCount, notificationCount]);

  // Fetch notifications with proper dependency on activeTab
  const { data } = useQuery(
    ["notification", auth.panelistId, type],
    () => getNotificationApi(auth.panelistId, type),
    {
      enabled: showNotificationModel && (activeTab === 'Rewards' || activeTab === 'Survey'),
      refetchOnWindowFocus: false,
    }
  );

  // Mark notifications as read
  const markAsReadMutation = useMutation(
    (notificationIds: number[]) => markReadNotificationApi(auth.panelistId, notificationIds, type),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["notification", auth.panelistId, type]);
      },
    }
  );

  const handleModalClose = () => {
    setShowNotificationModel(false);
    if ((activeTab === 'Rewards' || activeTab === 'Survey') && data?.NotificationData) {
      // Automatically mark unread notifications as read
      const unreadNotifications = data.NotificationData.filter(
        (notification: any) => !notification.mark_as_read
      );
      if (unreadNotifications.length > 0) {
        const unreadIds = unreadNotifications.map((notification: any) => notification.id);
        markAsReadMutation.mutate (unreadIds);
      }
    }
  };

  // Handle tab change
  const handleTabChange = (tab: string) => {
    if (tab !== activeTab) {
      setActiveTab(tab);
      setPointNotiCount(getNotificationCount.data?.pointCount || [{ count: 0 }]);
      setSurveyNotiCount(getNotificationCount.data?.surveyCount || [{ count: 0 }]);
      // Increment tab change count
      setTabChangeCount((prevCount) => prevCount + 1);
    }
    queryClient.invalidateQueries(["notificationCount", auth.panelistId, tab]);
    queryClient.invalidateQueries(["notification", auth.panelistId, tab]);
  };

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        handleModalClose();
      }
    };

    if (showNotificationModel) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showNotificationModel, data, activeTab]);

  const tabs = [
    { key: "Rewards", label: t("LovedAboutUs.rewards.title.1"), hasUnread: pointNotiCount[0]?.count > 0 },
    { key: "Survey", label: t("point.notification.Survey"), hasUnread: surveyNotiCount[0]?.count > 0 },
    { key: "General", label: t("point.notification.General"), hasUnread: false },
    { key: "Support", label: `Support${supportCount ? ` (${supportCount})` : ''}`, hasUnread: supportCount > 0 },
  ];

  return (
    <div
      className="fixed z-50 flex flex-col overflow-hidden left-2 right-2 sm:left-auto sm:right-4 lg:right-6 sm:w-[400px] top-[calc(var(--zs-topbar-h)+8px)] max-h-[calc(100vh-var(--zs-topbar-h)-24px)] zs-popover"
      ref={modalRef}
    >
      <div className="flex items-center justify-between gap-3 px-5 pt-5 pb-4">
        <div className="flex items-center gap-3"><span className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary-soft text-primary-text"><FiBell size={19} /></span><div><h2 className="text-base font-semibold font-display text-ink">{t("point.notification.available2")}</h2><p className="mt-0.5 text-xs text-ink-3">Your latest activity, in one place</p></div></div>
        <button onClick={handleModalClose} className="zs-icon-btn !w-8 !h-8" aria-label="Close notifications">
          <FiX />
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="px-5 pb-3 border-b border-line">
        <div className="flex w-full zs-segmented" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              role="tab"
              aria-selected={activeTab === tab.key}
              onClick={() => handleTabChange(tab.key)}
              className="relative flex-1"
            >
              {tab.label}
              {tab.hasUnread && activeTab !== tab.key && (
                <span className="absolute w-1.5 h-1.5 rounded-full top-1.5 right-2 bg-danger animate-pulse"></span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-[200px] p-4 overflow-y-auto zs-scroll" style={{ WebkitOverflowScrolling: "touch" }}>
      {activeTab === 'Support' && supportContent}
      {activeTab === "Rewards" && (
        <PointUIModel
          showNotificationModel={showNotificationModel}
          setNotificationCount={setNotificationCount}
          activeTab={activeTab}
        />
      )}
      {activeTab === "Survey" && (
        <SurveyUIModel
          showNotificationModel={showNotificationModel}
          setNotificationCount={setNotificationCount}
          activeTab={activeTab}
        />
      )}
      </div>
    </div>
  );
};
